import { mwLabel } from '../game/scoring';
import type { BestRun } from '../game/session';
import type { Archetype, Mode, Tier } from '../game/types';
import { str } from './strings';

export interface ShareContext {
  mode: Mode;
  /** Daily grid number; ignored in practice mode. */
  day: number | null;
  /** The player is on today's grid (not the archive) — streaks only apply there. */
  isToday: boolean;
  best: BestRun;
  firstMW: number | null;
  par: number;
  tier: Tier;
  beatReference: boolean;
  /** The best layout matched the reference. */
  matched: boolean;
  /** The day's shape, glyphed on the first line. */
  archetype: Archetype;
  /** The cold read and every new best after it, oldest first. */
  trail: number[];
  /** Name of the reference's topology, shown only on a perfect (GDD §3.5). */
  topology: string | null;
  /**
   * How many grids this sitting has run to, counting the one being shared.
   *
   * A count of the visit, not the grid — the same kind of claim the streak
   * makes at a different timescale (GDD §6.3).
   */
  chain: number;
  /** Reached the perfect grid having never removed a tower. */
  flawless: boolean;
  /** The reference was studied before this grid was perfected. */
  studied: boolean;
  streak: number;
}

/**
 * The day's shape, as the share names it.
 *
 * Emoji live here and nowhere else in the product (STYLEGUIDE §6): the share
 * renders in someone else's chat, not in this UI. The glyph says what
 * everyone was up against, which is not a spoiler — the map is shared, the
 * route is the secret.
 */
const ARCHETYPE_GLYPH: Record<Archetype, string> = {
  river: '🌊',
  delta: '🏞',
  canyon: '⛰',
  archipelago: '🏝',
  radial: '🧭',
};

/** Most milestones the MW line carries, however long the session ran. */
const MAX_MILESTONES = 5;

/**
 * Thin a run of readings to `n`, always keeping the first and the last.
 *
 * The endpoints are the ones that carry meaning — where the player started
 * cold and where they finished — so the thinning takes from the middle.
 */
export function thin(trail: number[], n = MAX_MILESTONES): number[] {
  if (trail.length <= n) return trail;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    out.push(trail[Math.round((i * (trail.length - 1)) / (n - 1))]);
  }
  return out;
}

/**
 * The brag. Spoiler-free by design — it reports how the grid was fought and
 * how well it was tuned, never where the towers went.
 */
export function buildShareText(ctx: ShareContext): string {
  const { best, firstMW, tier, trail } = ctx;

  // `84→96` compressed a fight to its endpoints; the milestones show the
  // fight. A run the game has no milestones for keeps the endpoints it does
  // have rather than inventing the middle (GDD Pillar 4).
  const target = ctx.matched ? ' 🎯' : '';
  const mw =
    firstMW != null && firstMW === best.mw
      ? str().shareMWFirstTake(mwLabel(best.mw))
      : trail.length >= 2
        ? str().shareMW(thin(trail).map(mwLabel).join(' · '), target)
        : firstMW != null
          ? str().shareMW(`${mwLabel(firstMW)} · ${mwLabel(best.mw)}`, target)
          : str().shareMW(mwLabel(best.mw), target);

  const rank =
    `${tier.emoji} ${str().ranksFull[tier.rank]}` +
    (ctx.topology ? ` · ${ctx.topology}` : '') +
    (ctx.beatReference ? ' · ' + str().shareBeatRef : '');

  // No emoji for the towers: Unicode has nothing for a transmission tower (🗼
  // is Tokyo Tower), and the share is plain text, so it can't use the drawn
  // glyph the page uses. A word is more honest than the wrong picture.
  const lines = [
    str().shareTitle(
      ctx.mode === 'daily' ? '#' + ctx.day : str().sharePractice,
      ARCHETYPE_GLYPH[ctx.archetype],
    ),
    mw,
    rank,
  ];

  const marks = [
    ctx.flawless ? str().shareFlawless : '',
    ctx.studied ? str().shareStudied : '',
  ].filter(Boolean);
  if (marks.length) lines.push(marks.join(' · '));

  // A sitting worth mentioning. Two grids is an afternoon; the line starts
  // where the number is the interesting part of the sentence, and it counts
  // where the player is now rather than where they end up — the chain is
  // still running when they share (GDD §6.3).
  if (ctx.chain >= CHAIN_FLOOR) lines.push(str().shareChain(ctx.chain));

  if (ctx.mode === 'daily' && ctx.isToday && ctx.streak > 1) {
    lines.push(str().shareStreak(ctx.streak));
  }
  // No link here: where it goes depends on how the result travels, and that
  // is `shareResult`'s to decide (GDD §6.3).
  return lines.join('\n');
}

/** Clipboard API where available, with the old textarea trick as a fallback. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    /* blocked or unavailable — try the legacy path */
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    ta.remove();
  }
}

/** How a result left the building — or didn't. */
/**
 * Where a shared result points, tagged so the arrivals it brings are
 * countable. `?r=` is the same convention every posted link uses, and the tag
 * is read once on a first visit (src/state/identity.ts) — so this counts
 * people a share actually brought, never a player coming back by their own
 * bookmark.
 *
 * Exported for the test that asserts the readings themselves carry no link:
 * which path it rides is this module's decision, not the text's.
 */
export const SHARE_URL = 'https://wirespan.app/?r=share';

/**
 * Grids in one sitting before the share says so.
 *
 * Below this the line is noise: nearly everyone plays one, and "1 grid today"
 * is a sentence about nothing. Three is where the count starts describing a
 * habit rather than a visit.
 */
const CHAIN_FLOOR = 3;

/**
 * The claim beside the share button, or `null` for silence (STYLEGUIDE §7).
 *
 * One true fact, chosen by rarity: the flawless mark against the day's own
 * room, else a sitting the road has run to, else nothing. A prompt that fires
 * on every result is chrome; one that fires when something happened is news.
 * The standing percentile is deliberately not among the claims — the field's
 * banded maths reads most of a top-heavy room as the same number, and a claim
 * everyone can make sells nothing.
 */
export type ShareClaim = { kind: 'flawless'; oneIn: number } | { kind: 'chain'; grids: number };

/**
 * "1 in N" starts being a rarity here. Below it the mark is a crowd: "1 in 2"
 * describes half the room, and a brag half the room can make is not one.
 */
const RARITY_FLOOR = 3;

export function claimFor(opts: {
  mode: Mode;
  /** The card is today's daily — an archive replay is a daily on an old day. */
  isToday: boolean;
  /** This result kept the flawless mark. */
  flawless: boolean;
  /** The day's room with this player in it, or `null` before the counters land. */
  room: { players: number; flawless: number } | null;
  /** Grids this sitting has run to, the one on the card included. */
  chain: number;
}): ShareClaim | null {
  // A drill's card has no share at all, so it has nothing to argue for.
  if (opts.mode === 'drill') return null;
  // "Today" is the claim's own word, so a settled archive room never wears
  // it: replays and practice get the chain line or none.
  if (
    opts.mode === 'daily' &&
    opts.isToday &&
    opts.flawless &&
    opts.room &&
    opts.room.players > 0
  ) {
    const oneIn = Math.round(opts.room.players / Math.max(1, opts.room.flawless));
    // On a day the mark is common the claim self-censors, and the sitting
    // gets its turn instead.
    if (oneIn >= RARITY_FLOOR) return { kind: 'flawless', oneIn };
  }
  if (opts.chain >= CHAIN_FLOOR) return { kind: 'chain', grids: opts.chain };
  return null;
}

export type ShareOutcome = 'shared' | 'copied' | 'dismissed' | 'failed';

/** Whether this platform hands results to a native share sheet. */
export function canShareSheet(): boolean {
  return typeof navigator.share === 'function';
}

/**
 * Hand the result to the platform's share sheet where one exists, the
 * clipboard elsewhere. The sheet carries the text and the game's URL. A
 * dismissed sheet is a change of mind, not a failure; a sheet that refuses
 * outright falls back to the clipboard.
 */
export async function shareResult(text: string): Promise<ShareOutcome> {
  // A sheet gets the link as a field of its own, which is what lets a target
  // build a preview card out of the page's og: image. The clipboard gets it
  // appended to the text instead, because a pasted result with no link is a
  // result nobody can act on. Each path carries it exactly once (GDD §6.3).
  const payload = { text, url: SHARE_URL };
  if (
    canShareSheet() &&
    (typeof navigator.canShare !== 'function' || navigator.canShare(payload))
  ) {
    try {
      await navigator.share(payload);
      return 'shared';
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return 'dismissed';
      /* NotAllowedError and friends: the sheet refused — the clipboard won't */
    }
  }
  return (await copyText(`${text}\n${SHARE_URL}`)) ? 'copied' : 'failed';
}
